var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { t } from '@lingui/core/macro';
import { useLeaveDocumentPrompt } from 'app/domains/esigning/hooks/useLeaveDocumentPrompt';
import { ApprovalErrorSubcodes } from 'app/domains/esigning/utils';
import { useAccount } from 'shared/domains/users';
import { CreateSignRequestDocument } from './mutation.gql';
import { useGetPdf } from './useGetPdf';
import { GetDocumentAttachmentKindsDocument } from '../../../queries/documentAttachmentKinds/kinds.gql';
import { CreateSignRequestErrors } from '../types';
const IGNORE_CODES = ['BAD_USER_INPUT', ApprovalErrorSubcodes.LacksApproval];
export function useCreateSignRequest() {
    var _a, _b;
    const { data: accountData } = useAccount();
    const { getPdf } = useGetPdf();
    const [loading, setLoading] = useState(false);
    const [mutate] = useMutation(CreateSignRequestDocument);
    const userId = (_a = accountData.userId) !== null && _a !== void 0 ? _a : '';
    const companyId = (_b = accountData.companyId) !== null && _b !== void 0 ? _b : '';
    useLeaveDocumentPrompt({
        busy: loading,
        message: t({
            message: 'If you leave now your document might not be sent for e-signing',
            comment: 'Message for when the users leaves a document while it is being sent for e-signing',
        }),
    });
    const convertToPdf = (documentId) => __awaiter(this, void 0, void 0, function* () {
        var _c;
        const pdf = yield getPdf(companyId, documentId);
        if (!((_c = pdf === null || pdf === void 0 ? void 0 : pdf.signed) === null || _c === void 0 ? void 0 : _c.url)) {
            throw new Error(CreateSignRequestErrors.NoPdfConversion);
        }
        return {
            file: { path: pdf.path, name: pdf.name, type: pdf.type, size: pdf.size },
            url: pdf.signed.url,
        };
    });
    const createSignRequest = (documentId, pdf) => __awaiter(this, void 0, void 0, function* () {
        setLoading(true);
        const pdfData = pdf;
        const pdfInput = {
            companyId,
            documentId,
            mimeType: pdfData.type,
            name: pdfData.name,
            path: pdfData.path,
            size: pdfData.size,
        };
        return mutate({
            variables: {
                documentId,
                userId,
                pdfInput,
            },
            context: {
                sentry: {
                    ignore: IGNORE_CODES,
                },
            },
            update(cache, { data }) {
                var _a;
                // eslint-disable-next-line no-underscore-dangle
                if (((_a = data === null || data === void 0 ? void 0 : data.createSignRequest) === null || _a === void 0 ? void 0 : _a.__typename) === 'CreateSignRequestRejectionList') {
                    return;
                }
                const { signRequest } = (data === null || data === void 0 ? void 0 : data.createSignRequest) || {};
                if (!signRequest) {
                    return;
                }
                cache.modify({
                    id: cache.identify({ id: documentId, __typename: 'FileSystemDocument' }),
                    fields: {
                        lastSignRequest(_existing, { toReference }) {
                            return toReference(signRequest, true);
                        },
                    },
                });
            },
            refetchQueries: [
                {
                    query: GetDocumentAttachmentKindsDocument,
                    variables: { documentId },
                },
            ],
            onQueryUpdated(observableQuery) {
                return observableQuery.refetch();
            },
        }).finally(() => setLoading(false));
    });
    return { createSignRequest, convertToPdf };
}
